import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, AlertTriangle, Calendar, Users, Mail, Bell, Info, Check } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Institution Message Composer
 * 
 * UI/UX Directive:
 * - Messages should feel like structured coordination, NOT chat
 * - Calm, clear interface
 * - Admin confirmation step for urgent notices
 * - Shows guidance: "Use messages for coordination and important updates. Overuse reduces clarity."
 */

const MESSAGE_TYPES = [
  { 
    id: 'announcement', 
    label: 'Announcement', 
    description: 'Informational updates to members',
    icon: Bell,
    color: 'var(--lk-sage)'
  },
  { 
    id: 'reminder', 
    label: 'Reminder', 
    description: 'Scheduled reminders for events or commitments',
    icon: Calendar,
    color: 'var(--lk-earth)'
  },
  { 
    id: 'coordination', 
    label: 'Coordination Message', 
    description: 'Logistics: supplies, volunteers, meeting updates',
    icon: Users,
    color: 'var(--lk-secondary)'
  },
  { 
    id: 'urgent_notice', 
    label: 'Urgent Notice', 
    description: 'Important updates requiring immediate attention',
    icon: AlertTriangle,
    color: '#D97706',
    warning: 'This is NOT an emergency alert system. For emergencies, contact emergency services.'
  }
];

const RECIPIENT_SCOPES = [
  { id: 'all_members', label: 'All Members' },
  { id: 'parents_only', label: 'Parents Only' },
  { id: 'specific_group', label: 'Specific Group' },
  { id: 'event_participants', label: 'Event Participants' }
];

const RESPONSE_SIGNALS = [
  { id: 'attending', label: "I'm attending" },
  { id: 'can_help', label: 'I can help' },
  { id: 'bringing_supplies', label: 'Bringing supplies' },
  { id: 'need_ride', label: 'Need a ride' },
  { id: 'not_attending', label: 'Not attending' }
];

export default function ComposeMessage() {
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingMessageId, setPendingMessageId] = useState(null);
  
  // Form state
  const [messageType, setMessageType] = useState('announcement');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientScope, setRecipientScope] = useState('all_members');
  const [deliveryChannels, setDeliveryChannels] = useState(['in_app']);
  const [enableResponses, setEnableResponses] = useState(false);
  const [responseOptions, setResponseOptions] = useState(['attending', 'not_attending']);
  const [requiresAcknowledgment, setRequiresAcknowledgment] = useState(false);
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  useEffect(() => {
    fetchInstitution();
  }, [institutionId]);
  
  const fetchInstitution = async () => {
    try {
      const res = await fetch(`${API}/api/inst-admin/${institutionId}/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInstitution(data);
      }
    } catch (err) {
      console.error('Failed to fetch institution:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleChannel = (channel) => {
    setDeliveryChannels(prev => 
      prev.includes(channel) 
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };
  
  const handleToggleResponse = (signalId) => {
    setResponseOptions(prev =>
      prev.includes(signalId)
        ? prev.filter(s => s !== signalId)
        : [...prev, signalId]
    );
  };
  
  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('Subject and message body are required');
      return;
    }
    
    setSending(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/institution-messages/institutions/${institutionId}/compose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message_type: messageType,
          subject: subject.trim(),
          body: body.trim(),
          recipient_scope: recipientScope,
          delivery_channels: deliveryChannels,
          enable_responses: enableResponses && messageType === 'coordination',
          response_options: responseOptions,
          requires_acknowledgment: requiresAcknowledgment
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send message');
      }
      
      if (data.requires_confirmation) {
        // Urgent notice needs confirmation
        setPendingMessageId(data.message_id);
        setShowConfirmation(true);
      } else {
        setSuccess('Message sent successfully');
        setTimeout(() => navigate(`/institutions/${institutionId}/messages`), 1500);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };
  
  const handleConfirmUrgent = async (confirmed) => {
    if (!confirmed) {
      setShowConfirmation(false);
      setPendingMessageId(null);
      return;
    }
    
    setSending(true);
    
    try {
      const res = await fetch(`${API}/api/institution-messages/institutions/${institutionId}/messages/${pendingMessageId}/confirm-urgent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message_id: pendingMessageId, confirmed: true })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send urgent notice');
      }
      
      setShowConfirmation(false);
      setSuccess(`Urgent notice sent to ${data.recipient_count} recipients`);
      setTimeout(() => navigate(`/institutions/${institutionId}/messages`), 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };
  
  const selectedType = MESSAGE_TYPES.find(t => t.id === messageType);
  const TypeIcon = selectedType?.icon || Bell;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 hover:opacity-70" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Compose Message</h1>
            <div className="w-10" />
          </div>
        </div>
      </header>
      
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Guidance Banner */}
        <Card className="p-4 mb-6 flex items-start gap-3" style={{ backgroundColor: 'var(--lk-sage-muted)', border: 'none' }}>
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-sage)' }} />
          <p className="text-sm" style={{ color: 'var(--lk-sage-dark)' }}>
            Use messages for coordination and important updates. Overuse reduces clarity.
          </p>
        </Card>
        
        {/* Institution Name */}
        {institution && (
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Sending from: <strong>{institution.name}</strong>
          </p>
        )}
        
        {/* Error/Success Messages */}
        {error && (
          <Card className="p-4 mb-4" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5' }}>
            <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
          </Card>
        )}
        
        {success && (
          <Card className="p-4 mb-4" style={{ backgroundColor: '#F0FDF4', border: '1px solid #86EFAC' }}>
            <p className="text-sm" style={{ color: '#16A34A' }}>{success}</p>
          </Card>
        )}
        
        {/* Message Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-3" style={{ color: 'var(--fy-text-primary)' }}>
            Message Type
          </label>
          <div className="grid grid-cols-2 gap-3">
            {MESSAGE_TYPES.map(type => {
              const Icon = type.icon;
              const isSelected = messageType === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => setMessageType(type.id)}
                  className="p-4 rounded-xl text-left transition-all"
                  style={{
                    backgroundColor: isSelected ? `${type.color}15` : 'var(--fy-surface-card)',
                    border: `2px solid ${isSelected ? type.color : 'var(--fy-border-subtle)'}`
                  }}
                  data-testid={`type-${type.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" style={{ color: type.color }} />
                    <span className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                      {type.label}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                    {type.description}
                  </p>
                </button>
              );
            })}
          </div>
          
          {/* Warning for Urgent Notice */}
          {messageType === 'urgent_notice' && (
            <Card className="p-3 mt-3 flex items-start gap-2" style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
              <p className="text-xs" style={{ color: '#92400E' }}>
                This is NOT an emergency alert system. For emergencies, contact emergency services directly.
              </p>
            </Card>
          )}
        </div>
        
        {/* Subject */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
            Subject
          </label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter message subject..."
            maxLength={200}
            className="w-full"
            data-testid="subject-input"
          />
        </div>
        
        {/* Body */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
            Message
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Enter your message..."
            maxLength={5000}
            rows={8}
            className="w-full rounded-lg p-3 text-sm resize-none"
            style={{ 
              backgroundColor: 'var(--fy-surface-muted)', 
              border: '1px solid var(--fy-border-subtle)',
              color: 'var(--fy-text-primary)'
            }}
            data-testid="body-input"
          />
          <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
            {body.length}/5000 characters
          </p>
        </div>
        
        {/* Recipient Scope */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
            Recipients
          </label>
          <div className="flex flex-wrap gap-2">
            {RECIPIENT_SCOPES.map(scope => (
              <button
                key={scope.id}
                onClick={() => setRecipientScope(scope.id)}
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{
                  backgroundColor: recipientScope === scope.id ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                  color: recipientScope === scope.id ? 'white' : 'var(--fy-text-secondary)'
                }}
                data-testid={`scope-${scope.id}`}
              >
                {scope.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Delivery Channels */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
            Delivery Channels
          </label>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={deliveryChannels.includes('in_app')}
                onChange={() => handleToggleChannel('in_app')}
                className="rounded"
              />
              <Bell className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
              <span className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>In-App</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={deliveryChannels.includes('email')}
                onChange={() => handleToggleChannel('email')}
                className="rounded"
              />
              <Mail className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
              <span className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>Email</span>
            </label>
          </div>
        </div>
        
        {/* Response Options (for coordination messages) */}
        {messageType === 'coordination' && (
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={enableResponses}
                onChange={(e) => setEnableResponses(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                Enable response signals
              </span>
            </label>
            
            {enableResponses && (
              <div className="ml-6 space-y-2">
                <p className="text-xs mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
                  Select which response options to show:
                </p>
                {RESPONSE_SIGNALS.map(signal => (
                  <label key={signal.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={responseOptions.includes(signal.id)}
                      onChange={() => handleToggleResponse(signal.id)}
                      className="rounded"
                    />
                    <span className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>{signal.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Acknowledgment (for school messages) */}
        {institution?.type === 'school' && (
          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresAcknowledgment}
                onChange={(e) => setRequiresAcknowledgment(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                Require parent acknowledgment
              </span>
            </label>
            <p className="text-xs mt-1 ml-6" style={{ color: 'var(--fy-text-secondary)' }}>
              Parents will be asked to confirm they have seen this message
            </p>
          </div>
        )}
        
        {/* Send Button */}
        <Button
          onClick={handleSend}
          disabled={sending || !subject.trim() || !body.trim()}
          className="w-full h-12 rounded-full flex items-center justify-center gap-2"
          style={{ backgroundColor: selectedType?.color || 'var(--lk-sage)', color: 'white' }}
          data-testid="send-button"
        >
          {sending ? (
            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <>
              <Send className="w-5 h-5" />
              {messageType === 'urgent_notice' ? 'Review & Send' : 'Send Message'}
            </>
          )}
        </Button>
      </main>
      
      {/* Urgent Notice Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
                <AlertTriangle className="w-6 h-6" style={{ color: '#D97706' }} />
              </div>
              <div>
                <h3 className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>Confirm Urgent Notice</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>This action cannot be undone</p>
              </div>
            </div>
            
            <p className="mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              This message will be marked <strong>urgent</strong> and sent to members immediately.
            </p>
            
            <p className="text-sm mb-6 p-3 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
              Note: LikeKind is a coordination tool. This message should not be relied upon as a replacement for emergency services.
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={() => handleConfirmUrgent(false)}
                variant="outline"
                className="flex-1"
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleConfirmUrgent(true)}
                className="flex-1"
                style={{ backgroundColor: '#D97706', color: 'white' }}
                disabled={sending}
                data-testid="confirm-urgent-button"
              >
                {sending ? 'Sending...' : 'Send Urgent Notice'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
